import { parse as parseCookie, serialize as serializeCookie } from 'hono/utils/cookie'
import { useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'

/** Theme preference persisted by the wallet. */
export type Theme = 'system' | 'light' | 'dark'

/** Cookie used to make the selected theme available during SSR. */
export const cookie = 'tempo-theme'

const listeners = new Set<() => void>()
let current: Theme = (() => {
  if (typeof window === 'undefined') return 'system'
  const theme = fromCookieHeader(document.cookie)
  if (theme !== 'system') return theme
  return parse(localStorage.getItem('theme'))
})()

if (typeof window !== 'undefined') {
  apply(current)
  write(current)
}

function notify() {
  for (const fn of listeners) fn()
}

/** Parses an unknown theme value into a supported preference. */
export function parse(value: string | null | undefined): Theme {
  if (value === 'light' || value === 'dark') return value
  return 'system'
}

/** Reads the persisted theme from a cookie header or document cookie string. */
export function fromCookieHeader(header: string | null | undefined): Theme {
  if (!header) return 'system'
  return parse(parseCookie(header, cookie)[cookie])
}

/** Returns the inline style needed to render a forced theme before hydration. */
export function style(theme: Theme): (CSSProperties & Record<`--${string}`, string>) | undefined {
  if (theme === 'system') return undefined
  return {
    colorScheme: theme,
    '--lightningcss-dark': theme === 'dark' ? 'initial' : ' ',
    '--lightningcss-light': theme === 'light' ? 'initial' : ' ',
  }
}

/** Returns the current theme and a setter. */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
    () => 'system' as Theme,
  )

  return [theme, set]
}

/** Sets the theme and notifies all subscribers. */
export function set(theme: Theme) {
  current = theme
  if (typeof window !== 'undefined') {
    apply(theme)
    write(theme)
  }
  notify()
}

/** Returns the resolved mode (what the user actually sees). */
export function resolved() {
  if (current !== 'system') return current
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function apply(theme: Theme) {
  const root = document.documentElement
  if (theme === 'system') {
    root.style.removeProperty('color-scheme')
    root.style.removeProperty('--lightningcss-light')
    root.style.removeProperty('--lightningcss-dark')
    return
  }
  root.style.colorScheme = theme
  // Lightning CSS compiles light-dark() into --lightningcss-light/dark vars
  // driven by prefers-color-scheme. Override them for manual theme switching.
  root.style.setProperty('--lightningcss-light', theme === 'light' ? 'initial' : ' ')
  root.style.setProperty('--lightningcss-dark', theme === 'dark' ? 'initial' : ' ')
}

function write(theme: Theme) {
  if (theme === 'system') {
    localStorage.removeItem('theme')
    document.cookie = serializeCookie(cookie, '', { path: '/', maxAge: 0, sameSite: 'Lax' })
    return
  }

  localStorage.setItem('theme', theme)
  document.cookie = serializeCookie(cookie, theme, {
    path: '/',
    maxAge: 31536000,
    sameSite: 'Lax',
  })
}
